import React from 'react';
import { Eye, MessageSquare, Mic, UserCog, PhoneOff, MicOff } from 'lucide-react';
import type { SupervisionType } from '../../lib/types/supervisor-monitoring';
import { useSupervisionTypeDescription, useCanPerformAction } from '../../lib/hooks/useSupervisorMonitoring';

interface SupervisorControlsProps {
  currentType: SupervisionType;
  onChangeType: (type: SupervisionType) => void;
  onEndSession: () => void;
  isMuted: boolean;
  onToggleMute: () => void;
  disabled?: boolean;
}

export function SupervisorControls({
  currentType,
  onChangeType,
  onEndSession,
  isMuted,
  onToggleMute,
  disabled = false,
}: SupervisorControlsProps) {
  const currentDescription = useSupervisionTypeDescription(currentType);
  
  const canChangeTo = (targetType: SupervisionType): boolean => {
    return useCanPerformAction(currentType, targetType);
  };

  const getModeButton = (
    type: SupervisionType,
    icon: React.ReactNode,
    label: string,
    description: string
  ): JSX.Element => {
    const isActive = currentType === type;
    const isAvailable = canChangeTo(type);

    return (
      <button
        onClick={() => onChangeType(type)}
        disabled={disabled || !isAvailable || isActive}
        className={`
          flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all
          ${
            isActive
              ? 'border-blue-500 bg-blue-50 shadow-md'
              : isAvailable
              ? 'border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50'
              : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
          }
        `}
        title={description}
      >
        <div className={`mb-2 ${isActive ? 'text-blue-600' : 'text-gray-600'}`}>
          {icon}
        </div>
        <span
          className={`text-sm font-medium ${
            isActive ? 'text-blue-900' : 'text-gray-700'
          }`}
        >
          {label}
        </span>
        {isActive && (
          <span className="mt-1 text-xs text-blue-600 font-medium">Active</span>
        )}
      </button>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Supervision Controls
        </h3>
        <p className="text-sm text-gray-600">
          Current Mode: <span className="font-medium">{currentDescription}</span>
        </p>
      </div>

      {/* Mode Selection Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {getModeButton(
          'monitor',
          <Eye className="w-6 h-6" />,
          'Monitor',
          'Silent monitoring - listen only'
        )}
        {getModeButton(
          'barge',
          <UserCog className="w-6 h-6" />,
          'Barge In',
          'Join the conversation - all parties hear you'
        )}
        {getModeButton(
          'whisper',
          <MessageSquare className="w-6 h-6" />,
          'Whisper',
          'Coach the agent - only agent hears you'
        )}
        {getModeButton(
          'takeover',
          <Mic className="w-6 h-6" />,
          'Takeover',
          'Take over the call - agent is muted'
        )}
      </div>

      {/* Audio Controls */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <button
          onClick={onToggleMute}
          disabled={disabled || currentType === 'monitor'}
          className={`
            flex items-center gap-2 px-4 py-2 rounded-lg transition-all
            ${
              isMuted
                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
        >
          {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          <span className="font-medium">
            {isMuted ? 'Unmute' : 'Mute'}
          </span>
        </button>

        <button
          onClick={onEndSession}
          disabled={disabled}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all"
        >
          <PhoneOff className="w-5 h-5" />
          <span className="font-medium">End Monitoring</span>
        </button>
      </div>

      {/* Mode Information */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Mode Descriptions:</h4>
        <ul className="space-y-1 text-xs text-gray-600">
          <li>
            <span className="font-medium">Monitor:</span> Listen to the call without
            being heard
          </li>
          <li>
            <span className="font-medium">Barge In:</span> Join the conversation with
            both agent and customer
          </li>
          <li>
            <span className="font-medium">Whisper:</span> Speak only to the agent for
            coaching
          </li>
          <li>
            <span className="font-medium">Takeover:</span> Take control of the call,
            muting the agent
          </li>
        </ul>
      </div>
    </div>
  );
}
